import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { apiClient } from '../utils/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { User as UserIcon, Mail, Phone, Shield, Save } from 'lucide-react';
import { toast } from 'sonner';

export default function ProfilePage() {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [twoFactorChallenge, setTwoFactorChallenge] = useState(null);
  const [twoFactorCode, setTwoFactorCode] = useState('');

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    }
  }, [user]);

const handleUpdateProfile = async (e) => {
  e.preventDefault();
  setLoading(true);
  
  try {
    // Update name/phone
    const profilePayload = {};
    if (formData.name !== user.name) profilePayload.name = formData.name;
    if (formData.phone !== (user.phone || '')) profilePayload.phone = formData.phone;
    
    if (Object.keys(profilePayload).length > 0) {
      await apiClient.patch('/auth/me', profilePayload);
    }
    
    // Change password if filled
    if (formData.newPassword) {
      if (formData.newPassword !== formData.confirmPassword) {
        toast.error('New passwords do not match');
        setLoading(false);
        return;
      }
      if (!formData.currentPassword) {
        toast.error('Enter your current password');
        setLoading(false);
        return;
      }
      await apiClient.post('/auth/change-password', {
        current_password: formData.currentPassword,
        new_password: formData.newPassword,
      });
    }
    
    toast.success('Profile updated successfully!');
    setFormData(prev => ({
      ...prev,
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    }));
  } catch (error) {
    toast.error(error?.response?.data?.detail || 'Failed to update profile');
  } finally {
    setLoading(false);
  }
};
  const getRoleBadgeColor = (role) => {
    const colors = {
      owner: 'bg-purple-100 text-purple-800 border-purple-200',
      pillar: 'bg-blue-100 text-blue-800 border-blue-200',
      employee: 'bg-green-100 text-green-800 border-green-200'
    };
    return colors[role] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const startTwoFactor = async () => {
    try { const res = await apiClient.post('/auth/2fa/request'); setTwoFactorChallenge(res.data.challenge_id); toast.success('A verification code was sent to your email.'); }
    catch (error) { toast.error(error?.response?.data?.detail || 'Unable to start 2FA setup'); }
  };
  const verifyTwoFactor = async () => {
    try { await apiClient.post('/auth/2fa/enable', { challenge_id: twoFactorChallenge, code: twoFactorCode }); toast.success('Email two-factor authentication enabled'); setTwoFactorChallenge(null); setTwoFactorCode(''); }
    catch (error) { toast.error(error?.response?.data?.detail || 'Invalid verification code'); }
  };

  return (
    <div className="space-y-4 lg:space-y-6" data-testid="profile-page">
      <div>
        <h1 className="text-3xl lg:text-4xl font-bold mb-2" style={{ letterSpacing: '-0.02em' }}>Profile Settings</h1>
        <p className="text-muted-foreground text-sm lg:text-base">Manage your account information</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
        {/* Profile Overview */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle>Account Overview</CardTitle>
            <CardDescription>Your account details</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-center">
              <div className="w-24 h-24 rounded-full bg-primary flex items-center justify-center">
                <span className="text-3xl font-bold text-primary-foreground">
                  {user?.name?.substring(0, 2).toUpperCase()}
                </span>
              </div>
            </div>
            <div className="text-center space-y-2">
              <h3 className="font-semibold text-lg">{user?.name}</h3>
              <Badge className={`${getRoleBadgeColor(user?.role)} capitalize text-xs`}>
                {user?.role}
              </Badge>
              <p className="text-sm text-muted-foreground flex items-center justify-center gap-2">
                <Mail className="w-4 h-4" />
                {user?.email}
              </p>
              {user?.phone && (
                <p className="text-sm text-muted-foreground flex items-center justify-center gap-2">
                  <Phone className="w-4 h-4" />
                  {user.phone}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Edit Profile Form */}
        <Card className="lg:col-span-1">
          <CardHeader><CardTitle>Email 2FA</CardTitle><CardDescription>Require an email code when signing in.</CardDescription></CardHeader>
          <CardContent className="space-y-3">
            <Badge variant="outline">{user?.two_factor_enabled ? 'Enabled' : 'Disabled'}</Badge>
            {!user?.two_factor_enabled && !twoFactorChallenge && <Button type="button" variant="outline" onClick={startTwoFactor}>Enable 2FA</Button>}
            {!user?.two_factor_enabled && twoFactorChallenge && <><Input inputMode="numeric" maxLength={6} placeholder="6-digit email code" value={twoFactorCode} onChange={(e) => setTwoFactorCode(e.target.value)} /><Button type="button" onClick={verifyTwoFactor}>Verify and enable</Button></>}
            {user?.two_factor_enabled && <Button type="button" variant="outline" onClick={async () => { await apiClient.post('/auth/2fa/disable'); toast.success('2FA disabled'); }}>Disable 2FA</Button>}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Edit Profile</CardTitle>
            <CardDescription>Update your personal information</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUpdateProfile} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="John Doe"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john@example.com"
                    disabled
                  />
                  <p className="text-xs text-muted-foreground">Email cannot be changed</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+1234567890"
                />
                <p className="text-xs text-muted-foreground">Include country code for SMS reminders</p>
              </div>

              <div className="pt-4 border-t">
                <h4 className="font-semibold mb-4 flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Change Password
                </h4>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="currentPassword">Current Password</Label>
                    <Input
                      id="currentPassword"
                      type="password"
                      value={formData.currentPassword}
                      onChange={(e) => setFormData({ ...formData, currentPassword: e.target.value })}
                      placeholder="Enter current password"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="newPassword">New Password</Label>
                      <Input
                        id="newPassword"
                        type="password"
                        value={formData.newPassword}
                        onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                        placeholder="Enter new password"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword">Confirm Password</Label>
                      <Input
                        id="confirmPassword"
                        type="password"
                        value={formData.confirmPassword}
                        onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                        placeholder="Confirm new password"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <Button type="submit" disabled={loading} className="flex-1 sm:flex-none">
                  <Save className="w-4 h-4 mr-2" />
                  {loading ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setFormData({
                      name: user?.name || '',
                      email: user?.email || '',
                      phone: user?.phone || '',
                      currentPassword: '',
                      newPassword: '',
                      confirmPassword: ''
                    });
                  }}
                  className="flex-1 sm:flex-none"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Account Information */}
      <Card>
        <CardHeader>
          <CardTitle>Account Information</CardTitle>
          <CardDescription>Additional details about your account</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground mb-1">User ID</p>
              <p className="font-mono text-xs">{user?.id}</p>
            </div>
            <div>
              <p className="text-muted-foreground mb-1">Account Created</p>
              <p>{user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}</p>
            </div>
            <div>
              <p className="text-muted-foreground mb-1">Account Type</p>
              <p className="capitalize">{user?.role}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
